import { useState, type ReactElement } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { Field, FieldLabel } from 'app/components/ui/field'
import { logError } from 'lib/logging'
import { GoldenEditor } from './GoldenEditor'
import { emptyGolden, type GoldenExtraction } from './mockData'
import { importCall } from './importCall'
import type { NewDatasetInput, NewItemInput } from './OpsDatasetsContext'

/** Slugifies a name into a candidate tag, e.g. "Difficult Names" → "difficult_names". */
function toTag(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, `_`)
    .replace(/^_+|_+$/g, ``)
}

export function NewDatasetModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (input: NewDatasetInput) => void
}): ReactElement {
  const [name, setName] = useState(``)
  const [description, setDescription] = useState(``)
  const [tag, setTag] = useState(``)
  const effectiveTag = tag || toTag(name)

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New dataset</DialogTitle>
          <DialogDescription>A dataset is a named tag query over labeled calls.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <Field>
            <FieldLabel>Name</FieldLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>Description</FieldLabel>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>Membership tag</FieldLabel>
            <Input value={effectiveTag} onChange={(e) => setTag(e.target.value)} placeholder="derived from name" />
            <span className="text-caption-sm text-minimal">Items carrying this tag belong to the dataset.</span>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="tertiary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!name.trim() || !effectiveTag}
            onClick={() => onCreate({ name: name.trim(), description: description.trim(), tag: effectiveTag })}
          >
            Create dataset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Everything the add-call form edits, in one object so Import can fill it in one update. */
type ItemDraft = {
  callId: string
  /** ISO timestamp. */
  callDate: string
  callerName: string
  transcript: string
  tagsText: string
  golden: GoldenExtraction
  /** GoldenEditor reads `initial` once, so a new seed remounts it under a new key. */
  goldenKey: number
}

type ImportStatus = { importing: boolean; message: { error: boolean; text: string } | null }

export function AddItemModal({
  defaultTag,
  onClose,
  onAdd,
}: {
  defaultTag?: string
  onClose: () => void
  onAdd: (input: NewItemInput) => void
}): ReactElement {
  const [draft, setDraft] = useState<ItemDraft>(() => ({
    callId: ``,
    callDate: new Date().toISOString(),
    callerName: ``,
    transcript: ``,
    tagsText: defaultTag ?? ``,
    golden: emptyGolden(),
    goldenKey: 0,
  }))
  const [importStatus, setImportStatus] = useState<ImportStatus>({ importing: false, message: null })

  const tags = draft.tagsText
    .split(`,`)
    .map((t) => t.trim())
    .filter(Boolean)

  const onImport = async () => {
    setImportStatus({ importing: true, message: null })
    try {
      const imported = await importCall(draft.callId.trim())
      setDraft((prev) => ({
        ...prev,
        callDate: imported.callDate,
        callerName: imported.callerName,
        transcript: imported.transcript,
        golden: imported.golden,
        goldenKey: prev.goldenKey + 1,
      }))
      setImportStatus({
        importing: false,
        message: {
          error: false,
          text: `Seeded from the call's current AI extraction. Correct it before you save: an uncorrected seed only measures agreement with the old model.`,
        },
      })
    } catch (error) {
      logError(`Call import failed`, error)
      setImportStatus({
        importing: false,
        message: { error: true, text: error instanceof Error ? error.message : String(error) },
      })
    }
  }

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add call to dataset</DialogTitle>
          <DialogDescription>Fill the golden extraction, then “Save golden” adds the item.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel>Call ID</FieldLabel>
              <div className="flex gap-2">
                <Input
                  value={draft.callId}
                  onChange={(e) => setDraft((prev) => ({ ...prev, callId: e.target.value }))}
                />
                <Button
                  variant="white"
                  size="sm"
                  loading={importStatus.importing}
                  disabled={!draft.callId.trim() || importStatus.importing}
                  onClick={onImport}
                >
                  Import
                </Button>
              </div>
            </Field>
            <Field>
              <FieldLabel>Caller name</FieldLabel>
              <Input
                value={draft.callerName}
                onChange={(e) => setDraft((prev) => ({ ...prev, callerName: e.target.value }))}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel>Transcript</FieldLabel>
            <Textarea
              rows={4}
              value={draft.transcript}
              onChange={(e) => setDraft((prev) => ({ ...prev, transcript: e.target.value }))}
            />
          </Field>
          {importStatus.message && (
            <p
              className={importStatus.message.error ? `text-caption-sm text-red-700` : `text-caption-sm text-amber-700`}
            >
              {importStatus.message.text}
            </p>
          )}
          <Field>
            <FieldLabel>Tags (comma-separated)</FieldLabel>
            <Input
              value={draft.tagsText}
              onChange={(e) => setDraft((prev) => ({ ...prev, tagsText: e.target.value }))}
            />
          </Field>

          <GoldenEditor
            key={draft.goldenKey}
            initial={draft.golden}
            onSave={(next) =>
              onAdd({
                callId: draft.callId.trim() || `call_${Date.now()}`,
                callDate: draft.callDate,
                callerName: draft.callerName.trim() || `Unknown Caller`,
                transcriptSnapshot: draft.transcript,
                tags,
                golden: next,
              })
            }
            onCancel={onClose}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
